import React, { useContext, useState, useCallback, useEffect, useRef } from 'react'
import { CSVLink } from 'react-csv'
import _ from 'lodash'
import { Button, Form, Spinner } from 'react-bootstrap'

import FiltersComponent from '../widgets/filters'
import AppConfigContext from '../app_layout/app_config_context'
import SearchByTagsTable from '../tables/search_tag/search_by_tags_list'
import CustomOverlayTrigger from '../widgets/overlay_trigger'

import * as Constants from '../../helpers/constants'
import { ApiHelper } from '../../helpers/apihelper'
import * as FiltersConstants from '../widgets/filters/constants'
import TablePagination from '../widgets/pagination'
import * as SearchByTagsConstants from './helpers/constants'
import showToastNotification from '../widgets/toastify'

import './../../static/css/search_by_tags.css'

const initialState = {
  data: [],
  isLoading: false,
  searchText: '',
  skip: 0,
  limit: 50,
  dataToDownload: '',
  filename: '',
  filters: {
    date_range: {
      filter_label: 'date_range',
      label: '',
      value: {
        key: 'tagged_on',
        preset: 'ps_last_7_days_including_today'
      }
    },
    tags_filter: ['sys:no_usage'],
  },
  pageCount: 0,
  currentPageIndex: 0,
  isDownloading: false
}

const filterParams = (filters) => {
  let queryFilter = {
    date_range: filters.date_range?.value || null,
    tags: _.isEmpty(filters.tags_filter) ? null : filters.tags_filter
  }
  return queryFilter
}

const pageSizeOptions = Constants.PAGE_SIZE.values.map((pageSize) => ({
  label: pageSize.toString(),
  value: pageSize,
}))


const SearchByTags = () => {

  const [state, setState] = useState(_.cloneDeep(initialState))

  const csvLinkRef = useRef(null)

  const appConfig = useContext(AppConfigContext)
  const client = appConfig.client

  const clientDisplayName = Constants.CLIENTS[client.toUpperCase()].DISPLAY_NAME
  const clientDownloadName = Constants.CLIENTS[client.toUpperCase()].DOWNLOAD_NAME

  const columns = SearchByTagsConstants.SEARCH_BY_TAGS_COLUMNS[client]

  useEffect(
    () => {
      if (csvLinkRef.current && !_.isEmpty(state.dataToDownload)) {
        csvLinkRef.current.link.click()
        setState(prevState => ({ ...prevState, dataToDownload: [] })) // reset state.dataToDownload to empty
      }
    },
    [state.dataToDownload]
  )

  const getUsersByTags = useCallback(
    (requestBody, signal = null) => {

      let body = _.pick(requestBody, ApiHelper(client, 'SEARCH_BY_TAGS').API_BODY)

      ApiHelper(client, 'SEARCH_BY_TAGS').API(client, body, signal)
        .then((response) => {
          if (response && response.status === Constants.CO_API.SUCCESS) {

            setState(prevState => ({
              ...prevState,
              data: _.map(response.data.data, (item, index) => ({ ...item, sr_no: index + 1 })),
              pageCount: Math.ceil(_.get(response, 'data.count') / _.get(response, 'data.limit')),
              limit: _.get(response, 'data.limit'),
              isLoading: false
            }))
          } else if (response && response.status === Constants.CO_API.FAILED) {
            showToastNotification({
              message: 'Request Failed',
              isSuccess: false,
            })
            setState(prevState => ({
              ...prevState,
              isLoading: false
            }))
          }
        })
        .catch((error) => {
          console.log(error)
          if (error.code && error.code !== 'ERR_CANCELED') {
            setState(prevState => ({
              ...prevState,
              isLoading: false
            }))
          }
        })
    },
    [client]
  )

  useEffect(
    () => {
      const controller = new AbortController()
      const signal = controller.signal

      setState(prevState => ({ ...prevState, isLoading: true }))

      let requestBody = {
        skip: state.skip,
        limit: state.limit,
        search: state.searchText,
        filters: filterParams(state.filters)
      }

      getUsersByTags(requestBody, signal)

      // Cleanup function to abort the request if the component unmounts or dependencies change
      return () => {
        controller.abort()
      }
    },
    [state.limit, state.skip, state.searchText, state.filters, getUsersByTags]
  )

  const getClearoutSearchByTagUsersForDownload = (skip, limit, searchText) => {
    let requestBody = {
      skip: skip,
      limit: limit,
      search: searchText,
      filters: filterParams(state.filters),
      download: true,
    }

    let body = _.pick(requestBody, ApiHelper(client, 'SEARCH_BY_TAGS_USER_DOWNLOAD').API_BODY)

    if (body.filters.date_range && body.filters.date_range.preset) {
      setState(prevState => ({
        ...prevState,
        filename: (`${client}_users_by_tags_${body.filters.date_range.preset}_${new Date().toISOString().split('T')[0]}.csv`),
        isDownloading: true
      }))
    } else {
      setState(prevState => ({
        ...prevState,
        filename: (`${client}_users_by_tags_${new Date().toISOString().split('T')[0]}.csv`),
        isDownloading: true
      }))
    }

    ApiHelper(client, 'SEARCH_BY_TAGS_USER_DOWNLOAD').API(client, body)
      .then((response) => {
        if (response && response.status === Constants.CO_API.SUCCESS) {
          showToastNotification({
            message: 'Downloaded Users List With Tags Successfully',
            isSuccess: true,
            position: 'top-center'
          })
          setState(prevState => ({ ...prevState, dataToDownload: response.data.csv, isDownloading: false }))
        } else {
          showToastNotification({
            message: 'Request Failed',
            isSuccess: false,
          })
          setState(prevState => ({ ...prevState, isDownloading: false }))
        }
      })
      .catch((error) => {
        console.error('Download error:', error)
        setState(prevState => ({ ...prevState, isDownloading: false }))
      })
  }

  const clearSearch = () => {
    setState(prevState => ({ ...prevState, searchText: '' }))
  }

  const onSearch = (event) => {
    setState(prevState => ({ ...prevState, searchText: event.target.value }))
  }

  const onSetFilter = (filters) => {
    setState(prevState => ({ ...prevState, filters: { ...filters } }))
  }

  const onChangePageSize = (event) => {
    let pageSize = Number(event.target.value)
    setState(prevState => ({ ...prevState, limit: pageSize }))
  }

  const updatePagination = (pageIndex) => {
    setState(prevState => ({ ...prevState, skip: pageIndex * state.limit, currentPageIndex: pageIndex }))
  }

  return (
    <div className='search-by-tags__container'>
      <div className='d-flex justify-content-between mb-2'>
        <h1 className='header m-0'>
          {clientDisplayName} Search By Tags
        </h1>
        <div>
          <CSVLink
            data={state.dataToDownload}
            filename={state.filename}
            ref={csvLinkRef}
          >
          </CSVLink>
          {state.isDownloading ?
            <CustomOverlayTrigger
              key='Downloading'
              content={'Downloading'}
              placement="auto-start"
            >
              <Button>
                <Spinner
                  animation="border"
                  variant='warning'
                  size="sm"
                />
              </Button>
            </CustomOverlayTrigger>
            :
            <CustomOverlayTrigger
              key={clientDownloadName}
              content={clientDownloadName}
              placement="auto-start"
            >
              <Button
                onClick={() => getClearoutSearchByTagUsersForDownload(0, 10000, state.searchText)}
                disabled={_.isEmpty(state.data) || state.isLoading}
              >
                <i className="fa fa-download"></i>
              </Button>
            </CustomOverlayTrigger>
          }

        </div>
      </div>
      <div className='mb-2'>
        <FiltersComponent
          onSetFilters={onSetFilter}
          filterState={state.filters}
          onSearch={onSearch}
          resetFilters={_.cloneDeep(initialState).filters}
          clearSearch={clearSearch}
          searchValue={state.searchText}
          filtersToInclude={FiltersConstants.FILTERS_TO_INCLUDE[client].SEARCH_BY_TAGS}
          dateFieldKey="tagged_on"
          client={client}
          searchPlaceholder='Search by User Email or Id'
          renderedFrom='search_by_tags'
        />
      </div>
      {state.isLoading
        ?
        <div className='m-auto text-center'>
          <Spinner
            animation="border"
            variant='warning'
          />
        </div>
        :
        <div className='search-by-tags__table'>
          <SearchByTagsTable
            isLoading={state.isLoading}
            columns={columns}
            data={state.data}
          />
        </div>}
      <div className="d-flex justify-content-between mb-2">
        <div>
          <Form.Select
            value={state.limit}
            onChange={(event) => onChangePageSize(event)}
          >
            {pageSizeOptions.map((option) => (
              <option
                key={option.value}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </Form.Select>
        </div>
        <div>
          <TablePagination
            currentPageIndex={state.currentPageIndex}
            totalPage={state.pageCount}
            updateTableData={updatePagination}
          />
        </div>
      </div>
    </div>
  )
}

export default SearchByTags